import React, { useEffect, useRef, useState } from 'react';
import i18n, { tr } from '../../i18n/i18n';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../../redux/store';
import { useAppSelector } from '../../redux/stateHooks';
import TrialCountdown from '../TrialCountdown';
import { useTrialStatus } from '../../hooks/useTrialStatus';
import { AppButton } from '../atoms';

import NotificationPanel from '../NotificationPanel';
import { fetchNotifications } from '../../redux/action/notification-log.action';
import LanguageSwitcher from '../LanguageSelector/LanguageSelector';

const Navbar: React.FC<{
  handleMenuClick: () => void;
  title: string;
}> = ({ handleMenuClick, title }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const notificationPanelRef = useRef<HTMLDivElement>(null);
  const { totalUnread } = useSelector((state: RootState) => state.notificationLog);
  const user = useSelector((state: RootState) => state.auth.user);
  const { organization } = useAppSelector((state) => state.auth);

  // Note: Trial status polling handled centrally by useSubscriptionPolling in MainLayout
  // Fetch trial status from Redux store
  const { status: trialStatus, loading: trialLoading } = useTrialStatus();

  const handleUpgradeClick = () => {
    navigate('/billing-and-plans');
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationPanelRef.current &&
        !notificationPanelRef.current.contains(event.target as Node)
      ) {
        setNotificationPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Poll notifications every 5 minutes
  useEffect(() => {
    dispatch(fetchNotifications({}));
    const interval = setInterval(() => {
      dispatch(fetchNotifications({}));
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [dispatch]);

  const userImage =
    localStorage.getItem('userImage') || 'https://placehold.co/40x40/00a99d/ffffff?text=U';
  const dir = i18n.dir();

  return (
    // Keep the toolbar's icon layout stable across languages (menu+title on the
    // left, avatar/bell/switcher on the right); Tailwind's `space-x`/`ml-auto`
    // use physical margins that fight the browser's automatic RTL flex reversal,
    // which was bunching the page title into the icon cluster in Arabic. Only
    // the text content inside flips direction below.
    <nav dir="ltr" className="bg-white h-16 flex items-center shadow-md z-30 relative">
      <div className="w-full mx-auto px-4 flex items-center">
        {/* Left Side: Menu Icon and Page Title */}
        <div className="flex items-center flex-shrink-0 gap-4">
          <AppButton icon="pi pi-align-justify" text onClick={handleMenuClick} />
          <h1 dir={dir} className="text-xl font-semibold text-gray-800 whitespace-nowrap">
            {title}
          </h1>
        </div>

        {/* Right Side: Trial Countdown, User Avatar & Dropdown */}
        <div className="flex items-center flex-shrink-0 ml-auto gap-4">
          {/* Trial Countdown */}
          {!trialLoading && trialStatus && trialStatus.isTrial && (
            <TrialCountdown
              daysRemaining={trialStatus.trialDaysRemaining}
              onUpgradeClick={handleUpgradeClick}
            />
          )}

          {/* Language Switcher */}
          <LanguageSwitcher variant="compact" />

          {/* Notification Bell Icon */}
          <div className="relative" ref={notificationPanelRef}>
            <AppButton
              icon="pi pi-bell"
              rounded
              raised
              text
              size="large"
              onClick={() => setNotificationPanelOpen((prev) => !prev)}
            />
            {totalUnread > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 pointer-events-none">
                {totalUnread}
              </span>
            )}
            {notificationPanelOpen && (
              <NotificationPanel
                onNavigate={(route) => {
                  setNotificationPanelOpen(false);
                  navigate(route);
                }}
              />
            )}
          </div>
          {/* User Name and Email (hidden on smaller screens) */}
          <div className="hidden md:block text-right">
            <div className="font-semibold text-sm text-gray-800">
              {user?.first_name} {user?.last_name}
            </div>
            <div className="text-xs text-gray-500">{organization}</div>
          </div>

          {/* Avatar (logout moved to the sidebar bottom section) */}
          <div className="relative">
            <img
              src={userImage}
              alt={tr('common.user_avatar_alt', 'User Avatar')}
              className="h-10 w-10 rounded-full border-2 border-teal-400 object-cover"
            />
            <span className="absolute bottom-0 right-0 block h-3 w-3 rounded-full bg-green-500 ring-2 ring-white"></span>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
